import React, { useState, useEffect } from 'react';
import { FaTimes, FaSave, FaSpinner, FaPlus, FaTrash } from 'react-icons/fa';
import { CloseModal } from '../../sites/closemodal';

interface CenterFormData {
  center_name: string;
  contact_person: string;
  contact_phone: string;
  contact_email: string;
  address: string;
}

interface InstituteFormData {
  institute_name: string;
  institute_short_name: string;
  address: string;
  contact_person: string;
  contact_phone: string;
  contact_email: string;
  gst_number: string;
  centers: CenterFormData[];
}

interface AddInstituteOffcanvasProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: InstituteFormData) => void;
  loading?: boolean;
}

const emptyCenter = (): CenterFormData => ({
  center_name: '',
  contact_person: '',
  contact_phone: '',
  contact_email: '',
  address: '',
});

export const AddInstituteOffcanvas: React.FC<AddInstituteOffcanvasProps> = ({
  isOpen,
  onClose,
  onSubmit,
  loading = false
}) => {
  const [formData, setFormData] = useState<InstituteFormData>({
    institute_name: '',
    institute_short_name: '',
    address: '',
    contact_person: '',
    contact_phone: '',
    contact_email: '',
    gst_number: '',
    centers: [],
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [showCloseModal, setShowCloseModal] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setFormData({
        institute_name: '',
        institute_short_name: '',
        address: '',
        contact_person: '',
        contact_phone: '',
        contact_email: '',
        gst_number: '',
        centers: [],
      });
      setErrors({});
      setShowCloseModal(false);
    }
  }, [isOpen]);

  const requestClose = () => {
    if (loading) return;
    setShowCloseModal(true);
  };

  const confirmClose = () => {
    setShowCloseModal(false);
    onClose();
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (!formData.institute_name.trim()) newErrors.institute_name = 'Institute name is required';
    if (formData.contact_email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.contact_email))
      newErrors.contact_email = 'Invalid email format';
    if (formData.contact_phone && !/^[0-9]{10}$/.test(formData.contact_phone))
      newErrors.contact_phone = 'Phone must be 10 digits';

    const centerNames = new Set<string>();
    formData.centers.forEach((c, i) => {
      if (!c.center_name.trim()) {
        newErrors[`center_${i}_name`] = 'Center name is required';
      } else {
        const norm = c.center_name.trim().toLowerCase();
        if (centerNames.has(norm)) {
          newErrors[`center_${i}_name`] = 'Duplicate center name';
        }
        centerNames.add(norm);
      }
      if (c.contact_phone && !/^[0-9]{10}$/.test(c.contact_phone))
        newErrors[`center_${i}_phone`] = 'Phone must be 10 digits';
      if (c.contact_email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(c.contact_email))
        newErrors[`center_${i}_email`] = 'Invalid email format';
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const handleCenterChange = (index: number, field: keyof CenterFormData, value: string) => {
    setFormData(prev => {
      const centers = [...prev.centers];
      centers[index] = { ...centers[index], [field]: value };
      return { ...prev, centers };
    });
    const errKey = `center_${index}_${field === 'center_name' ? 'name' : field === 'contact_phone' ? 'phone' : field === 'contact_email' ? 'email' : field}`;
    if (errors[errKey]) setErrors(prev => ({ ...prev, [errKey]: '' }));
  };

  const addCenter = () => {
    setFormData(prev => ({ ...prev, centers: [...prev.centers, emptyCenter()] }));
  };

  const removeCenter = (index: number) => {
    setFormData(prev => ({ ...prev, centers: prev.centers.filter((_, i) => i !== index) }));
    const newErrors = { ...errors };
    Object.keys(newErrors).forEach(k => { if (k.startsWith(`center_${index}_`)) delete newErrors[k]; });
    setErrors(newErrors);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      const submitData: any = { ...formData };
      Object.keys(submitData).forEach(key => {
        if (key !== 'centers' && !submitData[key]) delete submitData[key];
      });
      submitData.centers = formData.centers.filter(c => c.center_name.trim());
      onSubmit(submitData);
    }
  };

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen && !loading) requestClose();
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, loading]);

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 z-50 transition-opacity" onClick={!loading ? requestClose : undefined} />
      <div className="fixed top-0 right-0 h-full w-full max-w-2xl bg-white shadow-xl z-50 transform transition-transform overflow-hidden flex flex-col">
        <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-gray-50">
          <div>
            <h2 className="text-xl font-semibold text-gray-800">Add Training Institute</h2>
            <p className="text-sm text-gray-600 mt-1">Fill in the details to add a new training institute.</p>
          </div>
          <button onClick={requestClose} className="p-2 hover:bg-gray-200 rounded transition-colors" disabled={loading}>
            <FaTimes className="w-5 h-5 text-gray-600" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Institute Name *</label>
              <input
                type="text" name="institute_name" value={formData.institute_name}
                onChange={handleInputChange} disabled={loading}
                className={`w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 text-sm ${errors.institute_name ? 'border-red-300 bg-red-50' : 'border-gray-300'}`}
                placeholder="Enter institute name"
              />
              {errors.institute_name && <p className="mt-1 text-sm text-red-600">{errors.institute_name}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Short Name</label>
              <input type="text" name="institute_short_name" value={formData.institute_short_name}
                onChange={handleInputChange} disabled={loading}
                className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 text-sm"
                placeholder="e.g., NSTI"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
              <textarea name="address" value={formData.address} onChange={handleInputChange} disabled={loading} rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 text-sm resize-none"
                placeholder="Enter full address"
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Contact Person</label>
                <input type="text" name="contact_person" value={formData.contact_person}
                  onChange={handleInputChange} disabled={loading}
                  className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 text-sm"
                  placeholder="Contact person name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Contact Phone</label>
                <input type="text" name="contact_phone" value={formData.contact_phone}
                  onChange={handleInputChange} disabled={loading}
                  className={`w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 text-sm ${errors.contact_phone ? 'border-red-300 bg-red-50' : 'border-gray-300'}`}
                  placeholder="10 digit phone" maxLength={10}
                />
                {errors.contact_phone && <p className="mt-1 text-sm text-red-600">{errors.contact_phone}</p>}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Contact Email</label>
              <input type="email" name="contact_email" value={formData.contact_email}
                onChange={handleInputChange} disabled={loading}
                className={`w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 text-sm ${errors.contact_email ? 'border-red-300 bg-red-50' : 'border-gray-300'}`}
                placeholder="email@example.com"
              />
              {errors.contact_email && <p className="mt-1 text-sm text-red-600">{errors.contact_email}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">GST Number</label>
              <input type="text" name="gst_number" value={formData.gst_number}
                onChange={handleInputChange} disabled={loading}
                className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 text-sm"
                placeholder="e.g., 27AABCU9603R1ZM" maxLength={15}
              />
            </div>

            {/* Centers Section */}
            <div className="border-t border-gray-200 pt-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-gray-700">Training Centers ({formData.centers.length})</h3>
                <button type="button" onClick={addCenter} disabled={loading}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded hover:bg-blue-100 transition-colors disabled:opacity-50">
                  <FaPlus className="w-3 h-3" /> Add Center
                </button>
              </div>

              {formData.centers.length === 0 && (
                <p className="text-sm text-gray-400 italic">No centers added. Click "Add Center" to add one.</p>
              )}

              {formData.centers.map((center, index) => (
                <div key={index} className="border border-gray-200 rounded p-4 mb-3 bg-gray-50">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-sm font-medium text-gray-700">Center {index + 1}</span>
                    <button type="button" onClick={() => removeCenter(index)} disabled={loading}
                      className="text-red-500 hover:text-red-700 transition-colors disabled:opacity-50">
                      <FaTrash className="w-4 h-4" />
                    </button>
                  </div>

                  <div className="space-y-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Center Name *</label>
                      <input type="text" value={center.center_name}
                        onChange={e => handleCenterChange(index, 'center_name', e.target.value)}
                        disabled={loading}
                        className={`w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 text-sm ${errors[`center_${index}_name`] ? 'border-red-300 bg-red-50' : 'border-gray-300'}`}
                        placeholder="Center name"
                      />
                      {errors[`center_${index}_name`] && <p className="mt-1 text-xs text-red-600">{errors[`center_${index}_name`]}</p>}
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Contact Person</label>
                        <input type="text" value={center.contact_person}
                          onChange={e => handleCenterChange(index, 'contact_person', e.target.value)}
                          disabled={loading}
                          className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 text-sm"
                          placeholder="Contact person"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Phone</label>
                        <input type="text" value={center.contact_phone}
                          onChange={e => handleCenterChange(index, 'contact_phone', e.target.value)}
                          disabled={loading} maxLength={10}
                          className={`w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 text-sm ${errors[`center_${index}_phone`] ? 'border-red-300 bg-red-50' : 'border-gray-300'}`}
                          placeholder="10 digit phone"
                        />
                        {errors[`center_${index}_phone`] && <p className="mt-1 text-xs text-red-600">{errors[`center_${index}_phone`]}</p>}
                      </div>
                    </div>

                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Email</label>
                      <input type="email" value={center.contact_email}
                        onChange={e => handleCenterChange(index, 'contact_email', e.target.value)}
                        disabled={loading}
                        className={`w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 text-sm ${errors[`center_${index}_email`] ? 'border-red-300 bg-red-50' : 'border-gray-300'}`}
                        placeholder="center@example.com"
                      />
                      {errors[`center_${index}_email`] && <p className="mt-1 text-xs text-red-600">{errors[`center_${index}_email`]}</p>}
                    </div>

                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Address</label>
                      <textarea value={center.address}
                        onChange={e => handleCenterChange(index, 'address', e.target.value)}
                        disabled={loading} rows={2}
                        className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 text-sm resize-none"
                        placeholder="Center address"
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </form>
        </div>

        <div className="border-t bg-gray-50 px-6 py-4 flex-shrink-0">
          <div className="flex flex-col sm:flex-row gap-3 sm:justify-end">
            <button type="button" onClick={requestClose} disabled={loading}
              className="w-full sm:w-auto px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50 transition-colors disabled:opacity-50">
              Cancel
            </button>
            <button type="button" onClick={handleSubmit} disabled={loading || !formData.institute_name.trim()}
              className="w-full sm:w-auto px-4 py-2 text-sm font-medium text-white bg-blue-800 rounded hover:bg-blue-950 transition-colors disabled:opacity-50 flex items-center justify-center gap-2">
              {loading ? (<><FaSpinner className="w-4 h-4 animate-spin" />Creating...</>) : (<><FaSave className="w-4 h-4" />Create Institute</>)}
            </button>
          </div>
        </div>
      </div>
      <CloseModal isOpen={showCloseModal} onClose={() => setShowCloseModal(false)} onConfirm={confirmClose} />
    </>
  );
};
